import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency, formatPercentage } from '@/lib/utils';
import KpiPreferencesService from '@/services/KpiPreferencesService';
import type {
  AssetKpiData,
  AssetKpiPreference,
  CannedKpi,
  CreateKpiPreferenceRequest,
} from '@/types/kpi';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  AlertTriangle,
  BarChart3,
  Building,
  Calculator,
  DollarSign,
  GripVertical,
  Home,
  Minus,
  Plus,
  Settings,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface CustomizableAssetKpisProps {
  assetId: string;
  assetData?: AssetKpiData;
  onRefresh?: () => void;
}

interface KpiCardProps {
  kpi: AssetKpiPreference;
  value: number | null;
  unit?: string;
  format?: 'currency' | 'percentage' | 'number' | 'ratio';
  benchmark?: number;
  error?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

// Function to get icon for KPI type (asset-level only)
const getKpiIcon = (kpiId: string) => {
  switch (kpiId) {
    // Financial Performance
    case 'cap_rate':
      return <Target className="h-4 w-4 text-muted-foreground" />;
    case 'cash_on_cash_return':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'gross_rent_multiplier':
      return <BarChart3 className="h-4 w-4 text-muted-foreground" />;
    case 'operating_expense_ratio':
      return <Calculator className="h-4 w-4 text-muted-foreground" />;
    case 'debt_service_coverage_ratio':
      return <Target className="h-4 w-4 text-muted-foreground" />;
    case 'appreciation_since_purchase':
      return <TrendingUp className="h-4 w-4 text-muted-foreground" />;
    case 'monthly_cash_flow':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'annual_cash_flow':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'net_cash_flow':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;

    // Property Metrics
    case 'price_to_rent_ratio':
      return <Home className="h-4 w-4 text-muted-foreground" />;
    case 'break_even_ratio':
      return <Calculator className="h-4 w-4 text-muted-foreground" />;
    case 'loan_to_value_ratio':
      return <Wallet className="h-4 w-4 text-muted-foreground" />;
    case 'price_per_square_foot':
      return <Home className="h-4 w-4 text-muted-foreground" />;
    case 'rent_per_square_foot':
      return <Home className="h-4 w-4 text-muted-foreground" />;
    case 'total_roi':
      return <BarChart3 className="h-4 w-4 text-muted-foreground" />;

    // Operational Metrics
    case 'occupancy_rate':
      return <Users className="h-4 w-4 text-muted-foreground" />;
    case 'vacancy_rate':
      return <Building className="h-4 w-4 text-muted-foreground" />;

    default:
      return <BarChart3 className="h-4 w-4 text-muted-foreground" />;
  }
};

const formatKPI = (
  value: number | null,
  format = 'number',
  unit?: string,
): string => {
  if (value === null || value === undefined) return '—';

  switch (format) {
    case 'currency':
      return formatCurrency(value);
    case 'percentage':
      return formatPercentage(value);
    case 'ratio':
      return `${value.toFixed(2)}x`;
    default:
      return unit
        ? `${value.toLocaleString()} ${unit}`
        : value.toLocaleString();
  }
};

const getBenchmarkComparison = (value: number, benchmark?: number) => {
  if (!benchmark || value === null) return null;

  const difference = ((value - benchmark) / benchmark) * 100;
  return {
    difference,
    isPositive: difference > 0,
    label: difference > 0 ? 'Above market' : 'Below market',
  };
};

const KpiCard = ({
  kpi,
  value,
  unit,
  format = 'number',
  benchmark,
  error,
  onEdit,
  onDelete,
}: KpiCardProps) => {
  const formattedValue = formatKPI(value, format, unit);
  const comparison = getBenchmarkComparison(value || 0, benchmark);
  const icon = getKpiIcon(kpi.kpi_id);

  return (
    <Card className="relative group hover:shadow-md transition-shadow !py-1 !px-2">
      <div className="flex flex-row items-center justify-between m-0">
        <div className="text-xs font-medium flex items-center gap-1 leading-tight m-0">
          {icon}
          {kpi.display_name}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-px m-0">
        <div className="text-xl font-bold m-0 leading-tight">
          {formattedValue}
        </div>
        {comparison && (
          <div className="flex items-center gap-1 text-xs m-0 leading-tight">
            <span
              className={
                comparison.isPositive ? 'text-green-600' : 'text-red-600'
              }
            >
              {comparison.isPositive ? '+' : ''}
              {comparison.difference.toFixed(1)}%
            </span>
            <span className="text-muted-foreground">vs market</span>
          </div>
        )}
        {error && (
          <Badge variant="destructive" className="text-xs m-0 leading-tight">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        )}
        {kpi.description && (
          <p className="text-xs text-muted-foreground m-0 leading-tight">
            {kpi.description}
          </p>
        )}
      </div>
    </Card>
  );
};

// Add KPI Card component
const AddKpiCard = ({
  onClick,
  disabled,
  className,
  isLimitReached,
}: {
  onClick: () => void;
  disabled: boolean;
  className?: string;
  isLimitReached: boolean;
}) => (
  <Card
    className={`border-dashed border-2 border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors cursor-pointer !py-1 !px-2 ${className}`}
    onClick={disabled ? undefined : onClick}
  >
    <div className="flex flex-col items-center justify-center text-center gap-px m-0">
      <Plus className="h-4 w-4 text-muted-foreground mb-1" />
      {isLimitReached ? (
        <>
          <h3 className="font-semibold text-muted-foreground text-xs m-0 leading-tight">
            KPI Limit Reached
          </h3>
          <p className="text-xs text-muted-foreground m-0 leading-tight">
            Maximum of 4 KPIs allowed per asset
          </p>
        </>
      ) : disabled ? (
        <>
          <h3 className="font-semibold text-muted-foreground text-xs m-0 leading-tight">
            All KPIs Added
          </h3>
          <p className="text-xs text-muted-foreground m-0 leading-tight">
            You've added all available KPIs
          </p>
        </>
      ) : (
        <>
          <h3 className="font-semibold text-xs m-0 leading-tight">Add KPI</h3>
          <p className="text-xs text-muted-foreground m-0 leading-tight">
            Add a KPI to track this asset's performance
          </p>
        </>
      )}
    </div>
  </Card>
);

export function CustomizableAssetKpis({
  assetId,
  assetData,
  onRefresh,
}: CustomizableAssetKpisProps) {
  const [preferences, setPreferences] = useState<AssetKpiPreference[]>([]);
  const [availableKpis, setAvailableKpis] = useState<CannedKpi[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState<string>('');
  const [customDisplayName, setCustomDisplayName] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [isAddingKpi, setIsAddingKpi] = useState(false);
  const [isRestoringDefaults, setIsRestoringDefaults] = useState(false);

  // Load asset KPI preferences
  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const prefs = await KpiPreferencesService.getAssetKpiPreferences(assetId);

      // If no preferences exist, set up defaults automatically
      if (prefs.length === 0) {
        try {
          await KpiPreferencesService.setupDefaultAssetKpis(assetId);
          // Reload preferences after setting up defaults
          const updatedPrefs =
            await KpiPreferencesService.getAssetKpiPreferences(assetId);
          setPreferences(updatedPrefs);
        } catch (error) {
          console.error('Error setting up default asset KPIs:', error);
          // Don't show error toast for default setup - it's expected for new assets
          setPreferences(prefs);
        }
      } else {
        setPreferences(prefs);
      }
    } catch (error) {
      console.error('Error loading asset KPI preferences:', error);
      toast.error('Failed to load KPI preferences');
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  // Load available KPIs (filtered for asset-level only)
  const loadAvailableKpis = useCallback(async () => {
    try {
      const allKpis = await KpiPreferencesService.getCannedKpis(
        undefined,
        'asset',
      );
      setAvailableKpis(allKpis);
    } catch (error) {
      console.error('Error loading available KPIs:', error);
      toast.error('Failed to load available KPIs');
    }
  }, []);

  useEffect(() => {
    loadPreferences();
    loadAvailableKpis();
  }, [loadPreferences, loadAvailableKpis]);

  // Handle restore defaults
  const handleRestoreDefaults = async () => {
    try {
      setIsRestoringDefaults(true);
      await KpiPreferencesService.setupDefaultAssetKpis(assetId);
      await loadPreferences();
      toast.success('Default KPIs restored');
    } catch (error: any) {
      console.error('Error restoring default KPIs:', error);
      toast.error('Failed to restore default KPIs');
    } finally {
      setIsRestoringDefaults(false);
    }
  };

  const handleAddKpi = async () => {
    if (!selectedKpi || isAddingKpi) return;

    // Check if already at 4 KPIs limit
    if (preferences.length >= 4) {
      toast.error('Maximum of 4 KPIs allowed per asset');
      return;
    }

    try {
      setIsAddingKpi(true);

      const cannedKpi = availableKpis.find((k) => k.name === selectedKpi);
      if (!cannedKpi) return;

      const preferenceData: CreateKpiPreferenceRequest = {
        scope: 'asset',
        kpi_id: selectedKpi,
        kpi_type: 'canned',
        display_name: customDisplayName || cannedKpi.display_name,
        description: customDescription || cannedKpi.description,
        configuration: {
          period: 'annual',
        },
        is_enabled: true,
        sort_order: preferences.length,
      };

      // Create the KPI preference
      const newPreference =
        await KpiPreferencesService.createAssetKpiPreference(
          assetId,
          preferenceData,
        );

      // Update state optimistically
      setPreferences((prev) => [...prev, newPreference]);

      // Reset form state
      setShowAddDialog(false);
      setSelectedKpi('');
      setCustomDisplayName('');
      setCustomDescription('');

      toast.success('KPI added successfully');
    } catch (error: any) {
      console.error('Error adding KPI:', error);
      if (error.response?.data?.error?.includes('Maximum of 4 KPIs')) {
        toast.error('Maximum of 4 KPIs allowed per asset');
      } else {
        toast.error('Failed to add KPI');
      }
    } finally {
      setIsAddingKpi(false);
    }
  };

  const handleDeleteKpi = async (kpiId: number) => {
    try {
      console.log('Deleting KPI with ID:', kpiId);

      // Optimistically remove from state
      setPreferences((prev) => prev.filter((p) => p.id !== kpiId));

      await KpiPreferencesService.deleteAssetKpiPreference(assetId, kpiId);
      toast.success('KPI removed successfully');
    } catch (error) {
      console.error('Error deleting KPI:', error);
      // Revert optimistic update on error
      await loadPreferences();
      toast.error('Failed to remove KPI');
    }
  };

  const getKpiValue = (kpi: AssetKpiPreference) => {
    if (!assetData) return null;

    // Map KPI IDs to asset data properties (asset-level only)
    const valueMap: Record<string, string> = {
      // Financial Performance
      cap_rate: 'cap_rate',
      cash_on_cash_return: 'cash_on_cash_return',
      gross_rent_multiplier: 'gross_rent_multiplier',
      operating_expense_ratio: 'operating_expense_ratio',
      debt_service_coverage_ratio: 'debt_service_coverage_ratio',
      appreciation_since_purchase: 'appreciation_since_purchase',
      monthly_cash_flow: 'monthly_cash_flow',
      annual_cash_flow: 'annual_cash_flow',
      net_cash_flow: 'net_cash_flow',

      // Property Metrics
      price_to_rent_ratio: 'price_to_rent_ratio',
      break_even_ratio: 'break_even_ratio',
      loan_to_value_ratio: 'loan_to_value_ratio',
      price_per_square_foot: 'price_per_square_foot',
      rent_per_square_foot: 'rent_per_square_foot',
      total_roi: 'total_roi',

      // Operational Metrics
      occupancy_rate: 'occupancy_rate',
      vacancy_rate: 'vacancy_rate',
    };

    const propertyKey = valueMap[kpi.kpi_id];
    return propertyKey ? assetData[propertyKey] : null;
  };

  const getKpiFormat = (kpi: AssetKpiPreference) => {
    const formatMap: Record<
      string,
      'currency' | 'percentage' | 'number' | 'ratio'
    > = {
      cap_rate: 'percentage',
      cash_on_cash_return: 'percentage',
      gross_rent_multiplier: 'ratio',
      price_to_rent_ratio: 'number',
      operating_expense_ratio: 'percentage',
      dscr: 'number',
      break_even_ratio: 'percentage',
      appreciation_since_purchase: 'percentage',
      loan_to_value_ratio: 'percentage',
      price_per_square_foot: 'currency',
      rent_per_square_foot: 'currency',
      total_roi: 'percentage',
      occupancy_rate: 'percentage',
      monthly_cash_flow: 'currency',
      annual_cash_flow: 'currency',
      net_cash_flow: 'currency',
      vacancy_rate: 'percentage',
    };

    return formatMap[kpi.kpi_id] || 'number';
  };

  const getKpiUnit = (kpi: AssetKpiPreference) => {
    const unitMap: Record<string, string> = {
      cap_rate: '%',
      cash_on_cash_return: '%',
      operating_expense_ratio: '%',
      break_even_ratio: '%',
      appreciation_since_purchase: '%',
      loan_to_value_ratio: '%',
      price_per_square_foot: '/sqft',
      rent_per_square_foot: '/sqft',
      total_roi: '%',
      occupancy_rate: '%',
      vacancy_rate: '%',
    };

    return unitMap[kpi.kpi_id];
  };

  const getKpiBenchmark = (kpi: AssetKpiPreference) => {
    const benchmarkMap: Record<string, number> = {
      cap_rate: 6.5,
      cash_on_cash_return: 8.0,
      gross_rent_multiplier: 12.0,
      price_to_rent_ratio: 15.0,
      operating_expense_ratio: 50.0,
      dscr: 1.25,
      break_even_ratio: 85.0,
      appreciation_since_purchase: 3.0,
      loan_to_value_ratio: 75.0,
      total_roi: 12.0,
      occupancy_rate: 95.0,
      vacancy_rate: 5.0,
    };

    return benchmarkMap[kpi.kpi_id];
  };

  if (loading) {
    return <div className="py-8 text-center">Loading KPIs...</div>;
  }

  // Get available KPIs that haven't been added yet
  const addedKpiIds = preferences.map((p) => p.kpi_id);
  const availableKpisToAdd = availableKpis.filter(
    (k) => !addedKpiIds.includes(k.name),
  );

  // Empty state when no KPIs are selected
  if (preferences.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
            <BarChart3 className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No KPIs Selected</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Add KPIs to track this asset's performance metrics, or restore the
            default set.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => setShowAddDialog(true)}
              disabled={availableKpisToAdd.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add KPI
            </Button>
            <Button
              variant="outline"
              onClick={handleRestoreDefaults}
              disabled={isRestoringDefaults}
            >
              {isRestoringDefaults ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Restoring...
                </>
              ) : (
                <>
                  <Settings className="h-4 w-4 mr-2" />
                  Restore Defaults
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Add KPI Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add KPI</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="kpi-select">Select KPI</Label>
                <Select value={selectedKpi} onValueChange={setSelectedKpi}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a KPI to add" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableKpisToAdd.map((kpi) => (
                      <SelectItem key={kpi.name} value={kpi.name}>
                        {kpi.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedKpi && (
                <>
                  <div>
                    <Label htmlFor="display-name">
                      Display Name (Optional)
                    </Label>
                    <Input
                      id="display-name"
                      value={customDisplayName}
                      onChange={(e) => setCustomDisplayName(e.target.value)}
                      placeholder="Custom display name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      id="description"
                      value={customDescription}
                      onChange={(e) => setCustomDescription(e.target.value)}
                      placeholder="Custom description"
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddKpi}
                  disabled={!selectedKpi || isAddingKpi}
                >
                  {isAddingKpi ? 'Adding...' : 'Add KPI'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {preferences.map((kpi) => (
          <KpiCard
            key={`${kpi.id}-${kpi.kpi_id}`}
            kpi={kpi}
            value={getKpiValue(kpi)}
            format={getKpiFormat(kpi)}
            unit={getKpiUnit(kpi)}
            benchmark={getKpiBenchmark(kpi)}
            onDelete={() => handleDeleteKpi(kpi.id)}
          />
        ))}

        {/* Add KPI Card - only show if under limit */}
        {preferences.length < 4 && (
          <AddKpiCard
            onClick={() => setShowAddDialog(true)}
            disabled={availableKpisToAdd.length === 0}
            isLimitReached={false}
          />
        )}
      </div>

      {/* Add KPI Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add KPI</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="kpi-select">Select KPI</Label>
              <Select value={selectedKpi} onValueChange={setSelectedKpi}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a KPI to add" />
                </SelectTrigger>
                <SelectContent>
                  {availableKpisToAdd.map((kpi) => (
                    <SelectItem key={kpi.name} value={kpi.name}>
                      {kpi.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedKpi && (
              <>
                <div>
                  <Label htmlFor="display-name">Display Name (Optional)</Label>
                  <Input
                    id="display-name"
                    value={customDisplayName}
                    onChange={(e) => setCustomDisplayName(e.target.value)}
                    placeholder="Custom display name"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    placeholder="Custom description"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddKpi}
                disabled={!selectedKpi || isAddingKpi}
              >
                {isAddingKpi ? 'Adding...' : 'Add KPI'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
